import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
	compose,
	withStateHandlers,
	withProps,
	mapProps // Ironic, isn't it?
} from 'recompose';
import { isNaN, get } from 'lodash';
import CSSTransition from 'react-transition-group/CSSTransition';
import TransitionGroup from 'react-transition-group/TransitionGroup';

import mapDispatchPropsToActions from 'store/helpers/mapDispatchToActions';
import { searchMapSelector } from 'store/selectors/search';
import { getLocalPhoneNumberByZipCode } from 'store/pageMeta/actions';
import { mapRedoSearchAction } from 'store/search/actions';

import { Text } from 'components/core/Text';
import { SFIcon } from 'components/core/SFIcon';
import { Spinner } from 'components/core/Spinner';
import { Button } from 'components/core/Button';

import { PaginationSummary } from 'components/core/Pagination';

import { Map } from './index';

import './map.scss';

export const enhance = compose(
	connect(
		searchMapSelector,
		{ getLocalPhoneNumberByZipCode, mapRedoSearchAction },
		mapDispatchPropsToActions
	),
	mapProps((props) => (!props.preselectedFacilityId ? props : { ...props, zoom: 10, searchOnMove: false })),
	withStateHandlers((props) => ({ showSpinner: get(props, 'showSpinner', true) }), {
		hideSpinner: () => () => ({ showSpinner: false }),
		handleRedoSearch: (_, { onSearch, actions }) => (bounds) => {
			if (bounds) {
				const { lat: cLat, lng: cLng } = bounds.getCenter().toJSON();
				const { lat: neLat, lng: neLng } = bounds.getNorthEast().toJSON();
				const { lat: swLat, lng: swLng } = bounds.getSouthWest().toJSON();

				const swValid = !isNaN(swLat) && !isNaN(swLng);
				const neValid = !isNaN(neLat) && !isNaN(neLng);
				const centerValid = !isNaN(cLat) && !isNaN(cLng);

				if (centerValid && neValid && swValid) {
					const boundary = {
						latitude: cLat,
						longitude: cLng,
						boundary: `${swLng} ${swLat} ${neLng} ${neLat}`
					};

					onSearch({ ...boundary });
				}
				actions.mapRedoSearchAction(true);
			}
		}
	}),
	withProps(({ searchLocation }) => {
		if (isNaN(parseFloat(searchLocation.latitude)) || isNaN(parseFloat(searchLocation.longitude))) {
			return { showing: false, showSpinner: false };
		}
		return {
			searchLocation: {
				lat: parseFloat(searchLocation.latitude),
				lng: parseFloat(searchLocation.longitude)
			}
		};
	})
);

export function SearchMap({
	actions, facilities, showSpinner, hideSpinner, searchLocation, zoom, handleRedoSearch, showing,
	facilitiesPerPage, totalFacilities, page, closeMap, localPhone, highlightedFacility, searchOnMove,
	preselectedFacilityId, googleMapsKey
}) {
	return (
		<div className="search-map">
			<Spinner show={showSpinner} />
			<TransitionGroup>
				{!!showing &&
					<CSSTransition
						classNames="search-map-animation"
						timeout={1000}
					>
						<div className="search-map-content">
							<div className="navigation">
								<div className="navigation-pill">
									<Button
										primary={false}
										className="back-to-list"
										onClick={closeMap}
									>
										<SFIcon icon="chevron-left" />
										<Text strong>Back to list view</Text>
									</Button>
									<PaginationSummary
										page={page}
										totalItems={totalFacilities}
										itemsPerPage={facilitiesPerPage}
										label="facilities"
										strong={false}
									/>
								</div>
							</div>
							<Map
								googleMapsKey={googleMapsKey}
								facilities={facilities}
								highlightedFacility={highlightedFacility}
								onReady={hideSpinner}
								searchLocation={searchLocation}
								initialZoom={zoom}
								onRedoSearch={handleRedoSearch}
								totalFacilities={totalFacilities}
								facilitiesPerPage={facilitiesPerPage}
								page={page}
								phone={localPhone}
								getLocalPhoneNumberByZipCode={actions.getLocalPhoneNumberByZipCode}
								searchOnMove={searchOnMove}
								preselectedFacilityId={preselectedFacilityId}
							/>
						</div>
					</CSSTransition>
				}
			</TransitionGroup>
		</div>
	);
}

SearchMap.propTypes = {
	actions: PropTypes.shape({
		getLocalPhoneNumberByZipCode: PropTypes.func
	}),
	searchLocation: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number
	}),
	facilities: PropTypes.arrayOf(PropTypes.object),
	highlightedFacility: PropTypes.number,
	showSpinner: PropTypes.bool,
	hideSpinner: PropTypes.func,
	zoom: PropTypes.number,
	handleRedoSearch: PropTypes.func,
	showing: PropTypes.bool,
	totalFacilities: PropTypes.number,
	facilitiesPerPage: PropTypes.number,
	page: PropTypes.number,
	closeMap: PropTypes.func,
	localPhone: PropTypes.object,
	searchOnMove: PropTypes.bool,
	preselectedFacilityId: PropTypes.number,
	googleMapsKey: PropTypes.string
};

export default enhance(SearchMap);
